import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import '../informs.css';
import './managerialCrossGeneral.css';
import FloatingActionButtons  from '../../floatingActionButtons/floatingActionButtons';
import { downloadPdf, openPdf } from '../utils/pdfGenerator';

import ManCrossPageCover         from './pages/ManCrossPageCover';
import ManCrossPageSummary       from './pages/ManCrossPageSummary';
import ManCrossPageRiskMap       from './pages/ManCrossPageRiskMap';
import ManCrossPageDiagnosis     from './pages/ManCrossPageDiagnosis';
import ManCrossPageKeyFindings   from './pages/ManCrossPageKeyFindings';
import ManCrossPagePareto        from './pages/ManCrossPagePareto';
import ManCrossPageSectionCover  from './pages/ManCrossPageSectionCover';
import ManCrossPageDimIPRA       from './pages/ManCrossPageDimIPRA';
import ManCrossPageHeatmapIPRA   from './pages/ManCrossPageHeatmapIPRA';
import ManCrossPageRankingIPRA   from './pages/ManCrossPageRankingIPRA';
import ManCrossPageDimIASH       from './pages/ManCrossPageDimIASH';
import ManCrossPageHeatmapIASH   from './pages/ManCrossPageHeatmapIASH';
import ManCrossPageProfilesIASH  from './pages/ManCrossPageProfilesIASH';
import ManCrossPageCofailIASH    from './pages/ManCrossPageCofailIASH';
import ManCrossPageIRHScatter    from './pages/ManCrossPageIRHScatter';
import ManCrossPageTopQ1         from './pages/ManCrossPageTopQ1';
import ManCrossPageNPS           from './pages/ManCrossPageNPS';
import ManCrossPageIVCByArea     from './pages/ManCrossPageIVCByArea';
import ManCrossPageRiskMatrix    from './pages/ManCrossPageRiskMatrix';
import ManCrossPageLeaderScatter from './pages/ManCrossPageLeaderScatter';
import ManCrossPageDriversImpact from './pages/ManCrossPageDriversImpact';
import ManCrossPageIcpTrend      from './pages/ManCrossPageIcpTrend';
import ManCrossPageRoiRiesgo     from './pages/ManCrossPageRoiRiesgo';
import ManCrossPagePlanGroup     from './pages/ManCrossPagePlanGroup';
import ManCrossPageActionPlan    from './pages/ManCrossPageActionPlan';
import ManCrossPageLegal         from './pages/ManCrossPageLegal';
import ManCrossPageClose         from './pages/ManCrossPageClose';
import {
    getMeasurementDate,
    getQuarterLabel,
    getMonthYearLabel,
    getCondensedEvaluationLabel,
    getIpraEvaluatedCount,
    getIashEvaluatedCount,
    buildSectionCoverInsight,
} from './shared/managerialUtils';

const ManagerialCrossGeneral = forwardRef(({ reportData, orgLogo, orgName, showFloatingActions = true }, ref) => {
    const pdfRef = useRef();
    const [pdfLoading,  setPdfLoading]  = useState(false);
    const [pdfProgress, setPdfProgress] = useState(null);

    const payload     = reportData?.payload || {};
    const evaluations = payload?.evaluations || {};

    // ── Dates derived from measurement start (not report created_at) ──────────
    const measurementDate = useMemo(
        () => getMeasurementDate(evaluations, reportData?.created_at),
        [evaluations, reportData?.created_at]
    );
    const reportYear    = measurementDate.getFullYear();
    const reportQuarter = getQuarterLabel(measurementDate);
    const monthYearLabel = useMemo(() => getMonthYearLabel(measurementDate), [measurementDate]);

    // ── Unique supervisors evaluated in IPRA ───────────────────────────────────
    const ipraEvaluatedCount = useMemo(
        () => getIpraEvaluatedCount(payload),
        [payload]
    );

    // ── Unique employees evaluated in IASH ────────────────────────────────────
    const iashEvaluatedCount = useMemo(
        () => getIashEvaluatedCount(payload),
        [payload]
    );

    // ── Section cover insights (dynamic, based on real dimension deficit) ──────
    const ipraTotals = payload?.totals?.ipra || {};
    const iashTotals = payload?.totals?.iash || {};
    const ipraInsight = useMemo(
        () => buildSectionCoverInsight(ipraTotals, 75, 'del déficit de liderazgo'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [payload]
    );
    const iashInsight = useMemo(
        () => buildSectionCoverInsight(iashTotals, 65, 'del déficit de autocuidado'),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [payload]
    );

    const ivcSepInsight = (() => {
        const ivcByArea = payload?.ivc_by_area || {};
        const rows = Object.entries(ivcByArea).map(([area, d]) => ({
            area,
            nivel: Number(d?.nivel ?? 0),
            icp: Number(payload?.icp_by_area?.[area]?.icp ?? 999),
        }));

        const active = rows.filter((r) => r.nivel >= 2);
        const nActive = active.length;

        if (nActive === 0) {
            return 'Sin áreas en co-falla activa. Mantener el foco en la prevención para asegurar que las barreras de seguridad sigan funcionando correctamente.';
        }

        const critical = active.filter((r) => r.nivel >= 3);
        const grave = (critical.length ? critical : active)
            .sort((a, b) => (b.nivel - a.nivel) || (a.icp - b.icp))[0];

        return `${nActive} áreas en co-falla activa. El accidente grave en ${grave?.area || 'el área crítica'} es predecible y prevenible si se actúa esta semana.`;
    })();

    // ── Page header text ───────────────────────────────────────────────────────
    const coverContext = useMemo(
        () => getCondensedEvaluationLabel(evaluations?.ipra?.title, evaluations?.iash?.title),
        [evaluations?.ipra?.title, evaluations?.iash?.title]
    );
    const summaryHeaderText = useMemo(() => {
        const parts = ['Sistema Predictivo de Seguridad Talentha'];
        if (orgName) parts[0] += ` - ${orgName}`;
        parts.push(monthYearLabel);
        return parts.join(' · ');
    }, [orgName, monthYearLabel]);

    // ── PDF ────────────────────────────────────────────────────────────────────
    const onProgress   = useCallback((c, t) => setPdfProgress({ current: c, total: t }), []);
    const pdfFilename  = `Informe Gerencial ${evaluations?.ipra?.title || 'IPRA'} + ${evaluations?.iash?.title || 'IASH'}.pdf`;

    const handleDownloadPDF = async () => {
        setPdfLoading(true);
        await downloadPdf(pdfRef.current, { filename: pdfFilename, orientation: 'landscape', onProgress });
        setPdfLoading(false);
        setPdfProgress(null);
    };

    const handleOpenPDF = async () => {
        setPdfLoading(true);
        await openPdf(pdfRef.current, { filename: pdfFilename, orientation: 'landscape', onProgress });
        setPdfLoading(false);
        setPdfProgress(null);
    };

    useImperativeHandle(ref, () => ({
        downloadPdf: handleDownloadPDF,
        openPdf: handleOpenPDF,
        getPdfFilename: () => pdfFilename,
    }), [pdfFilename]);

    return (
        <>
            <div className="informe-wrapper">
                <div ref={pdfRef} className="pdf-content mgcr-content">

                    {/* ── Página 1: Portada ── */}
                    <div className="pdf-page mgcr-page mgcr-page-cover">
                        <ManCrossPageCover
                            orgLogo={orgLogo}
                            orgName={orgName}
                            reportQuarter={reportQuarter}
                            reportYear={reportYear}
                        />
                    </div>

                    {/* ── Página 2: Resumen Ejecutivo ── */}
                    <div className="pdf-page mgcr-page mgcr-page-summary">
                        <ManCrossPageSummary
                            reportData={reportData}
                            orgName={orgName}
                            headerText={summaryHeaderText}
                            pageNumber={2}
                        />
                    </div>

                    {/* ── Página 3: Mapa de Riesgo IVC ── */}
                    <div className="pdf-page mgcr-page mgcr-page-riskmap">
                        <ManCrossPageRiskMap
                            reportData={reportData}
                            orgName={orgName}
                            headerText={summaryHeaderText}
                            pageNumber={3}
                        />
                    </div>

                    {/* ── Página 4: Diagnóstico Actual ── */}
                    <div className="pdf-page mgcr-page mgcr-page-diagnosis">
                        <ManCrossPageDiagnosis
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={4}
                        />
                    </div>

                    {/* ── Página 5: Hallazgos Clave ── */}
                    <div className="pdf-page mgcr-page mgcr-page-keyfindings">
                        <ManCrossPageKeyFindings
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={5}
                        />
                    </div>

                    {/* ── Página 6: Foco Estratégico / Pareto de Riesgo ── */}
                    <div className="pdf-page mgcr-page mgcr-page-pareto">
                        <ManCrossPagePareto
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={6}
                        />
                    </div>

                    <div className="pdf-page mgcr-page mgcr-page-scover">
                        <ManCrossPageSectionCover
                            pageNumber={2}
                            title="Liderazgo Preventivo"
                            subtitle="Evaluación IPRA"
                            insight={ipraInsight}
                            statValue={ipraEvaluatedCount || null}
                            statLabel={"Supervisores\nevaluados"}
                        />
                    </div>

                    {/* ── Página 8: Dimensiones IPRA ── */}
                    <div className="pdf-page mgcr-page mgcr-page-dimipra">
                        <ManCrossPageDimIPRA
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={8}
                        />
                    </div>

                    {/* ── Página 9: Heatmap IPRA por Área ── */}
                    <div className="pdf-page mgcr-page mgcr-page-heatmap">
                        <ManCrossPageHeatmapIPRA
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={9}
                        />
                    </div>

                    {/* ── Página 10: Ranking de Líderes ── */}
                    <div className="pdf-page mgcr-page mgcr-page-ranking">
                        <ManCrossPageRankingIPRA
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={10}
                        />
                    </div>

                    {/* ── Página 11: Sub-portada IASH ── */}
                    <div className="pdf-page mgcr-page mgcr-page-scover">
                        <ManCrossPageSectionCover
                            pageNumber={3}
                            title="Autocuidado y Riesgo en Operarios"
                            subtitle="Evaluación IASH"
                            insight={iashInsight}
                            statValue={iashEvaluatedCount || null}
                            statLabel={"Operarios\nevaluados"}
                        />
                    </div>

                    {/* ── Página 12: Dimensiones IASH ── */}
                    <div className="pdf-page mgcr-page mgcr-page-dimipra">
                        <ManCrossPageDimIASH
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={12}
                        />
                    </div>

                    {/* ── Página 13: Heatmap IASH por Área ── */}
                    <div className="pdf-page mgcr-page mgcr-page-heatmap">
                        <ManCrossPageHeatmapIASH
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={13}
                        />
                    </div>

                    {/* ── Página 14: Perfiles Dominantes por Área (IASH) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-profiles">
                        <ManCrossPageProfilesIASH
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={14}
                        />
                    </div>

                    {/* ── Página 15: Modelos Predictivos / Impacto de Co-falla (IASH) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-cofail">
                        <ManCrossPageCofailIASH
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={15}
                        />
                    </div>
                    {/* ── Página 16: Sub-portada IRH ── */}
                    <div className="pdf-page mgcr-page mgcr-page-scover">
                        <ManCrossPageSectionCover
                            pageNumber={4}
                            title="IRH"
                            subtitle="Índice de Riesgo Humano"
                            insight={null}
                            statValue={null}
                            statLabel={null}
                        />
                    </div>

                    {/* ── Página 17: IRH Scatter ── */}
                    <div className="pdf-page mgcr-page mgcr-page-irh-scatter">
                        <ManCrossPageIRHScatter
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={17}
                        />
                    </div>

                    {/* ── Página 18: Top 10 Operarios Q1 ── */}
                    <div className="pdf-page mgcr-page mgcr-page-topq1">
                        <ManCrossPageTopQ1
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={18}
                        />
                    </div>

                    {/* ── Página 19: Safety NPS ── */}
                    <div className="pdf-page mgcr-page mgcr-page-nps">
                        <ManCrossPageNPS
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={19}
                        />
                    </div>

                    {/* ── Página 20: Sub-portada IVC (SEP-D) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-scover">
                        <ManCrossPageSectionCover
                            pageNumber={5}
                            title="IVC — Co-falla de Barreras de Seguridad"
                            subtitle="Índice de Vulnerabilidad Compuesta"
                            insight={ivcSepInsight}
                            statValue={null}
                            statLabel={null}
                        />
                    </div>

                    {/* ── Página 21: Co-falla por Área (S18) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-ivc-area">
                        <ManCrossPageIVCByArea
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={21}
                        />
                    </div>

                    {/* ── Página 22: Matriz Crítica (S19) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-matrix">
                        <ManCrossPageRiskMatrix
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={22}
                        />
                    </div>

                    {/* ── Página 23: Correlación de Riesgo (S20) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-leader-scatter">
                        <ManCrossPageLeaderScatter
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={23}
                        />
                    </div>

                    {/* ── Página 24: Drivers de Impacto (S21) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-drivers-impact">
                        <ManCrossPageDriversImpact
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={24}
                        />
                    </div>

                    {/* ── Página 25: Tendencia ICP (S22) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-icp-trend">
                        <ManCrossPageIcpTrend
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={25}
                        />
                    </div>

                    {/* ── Página 26: ROI del Riesgo (S23) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-drivers-impact">
                        <ManCrossPageRoiRiesgo
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={26}
                        />
                    </div>

                    {/* ── Página 27: Plan Diferenciado por Grupo (S24) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-drivers-impact">
                        <ManCrossPagePlanGroup
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={27}
                        />
                    </div>

                    {/* ── Página 28: Plan de Acción 90 Días (S25) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-drivers-impact">
                        <ManCrossPageActionPlan
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={28}
                        />
                    </div>

                    {/* ── Página 29: Marco Legal (S26) ── */}
                    <div className="pdf-page mgcr-page mgcr-page-legal">
                        <ManCrossPageLegal
                            reportData={reportData}
                            headerText={summaryHeaderText}
                            pageNumber={29}
                        />
                    </div>

                    {/* ── Página Final: Cierre ── */}
                    <div className="pdf-page mgcr-page mgcr-page-close">
                        <ManCrossPageClose
                            orgLogo={orgLogo}
                            orgName={orgName}
                        />
                    </div>
                </div>
            </div>

            {showFloatingActions && (
                <FloatingActionButtons
                    handleDownloadPDF={handleDownloadPDF}
                    handleOpenPDF={handleOpenPDF}
                    handleExportExcel={() => {}}
                    pdfLoading={pdfLoading}
                    pdfProgress={pdfProgress}
                />
            )}
        </>
    );
});

export default ManagerialCrossGeneral;

